import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Check, Tag as TagIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_OPTIONS = [
  { token: 'saffron', hex: '#F5A623' },
  { token: 'mint',    hex: '#159C5C' },
  { token: 'navy',    hex: '#1B375B' },
  { token: 'teal',    hex: '#659A9B' },
  { token: 'amber',   hex: '#92400e' },
  { token: 'violet',  hex: '#7C3AED' },
];

const emptyForm = {
  slug: '',
  name: '',
  color: 'saffron',
  description: '',
  active: true,
  display_order: 100,
};

const ColorSwatch = ({ token, selected, onClick }) => {
  const opt = COLOR_OPTIONS.find((c) => c.token === token);
  if (!opt) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`occasion-color-${token}`}
      title={token}
      className={`w-9 h-9 rounded-full border-2 transition ${selected ? 'border-[#1B375B] ring-2 ring-[#1B375B]/30' : 'border-gray-200 hover:border-gray-400'}`}
      style={{ backgroundColor: opt.hex }}
    >
      {selected && <Check className="w-4 h-4 text-white mx-auto" strokeWidth={3} />}
    </button>
  );
};

const Ribbon = ({ name, color }) => {
  const opt = COLOR_OPTIONS.find((c) => c.token === color) || COLOR_OPTIONS[0];
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: opt.hex }}
    >
      <TagIcon className="w-3 h-3" /> {name}
    </span>
  );
};

const OccasionLabelsAdmin = () => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState('all'); // all|true|false
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchLabels = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterActive !== 'all') params.active = filterActive;
      const res = await axios.get(`${API}/admin/occasion-labels`, {
        withCredentials: true,
        params,
      });
      setLabels(res.data.labels || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load occasion labels');
    } finally {
      setLoading(false);
    }
  }, [filterActive]);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (label) => {
    setEditingId(label.id);
    setForm({
      slug: label.slug,
      name: label.name,
      color: label.color,
      description: label.description || '',
      active: label.active,
      display_order: label.display_order ?? 100,
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!editingId && !/^[a-z0-9][a-z0-9-]*$/.test(form.slug)) {
      setError('Slug must be lowercase letters, numbers and hyphens only (e.g. "arbaeen").');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          color: form.color,
          description: form.description || null,
          active: form.active,
          display_order: Number(form.display_order) || 100,
        };
        await axios.patch(`${API}/admin/occasion-labels/${editingId}`, payload, { withCredentials: true });
      } else {
        const payload = {
          slug: form.slug,
          name: form.name,
          color: form.color,
          description: form.description || null,
          active: form.active,
          display_order: Number(form.display_order) || 100,
        };
        await axios.post(`${API}/admin/occasion-labels`, payload, { withCredentials: true });
      }
      closeForm();
      fetchLabels();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to save label.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (label) => {
    if (!window.confirm(`Soft-delete "${label.name}"?\n\nIt will be hidden from new inventory forms but existing inventory rows still show the cached name & colour.`)) return;
    try {
      await axios.delete(`${API}/admin/occasion-labels/${label.id}`, { withCredentials: true });
      fetchLabels();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to delete label.');
    }
  };

  return (
    <div className="p-6" data-testid="occasion-labels-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1B375B]">Occasion Labels</h1>
            <p className="text-gray-600 mt-1 text-sm">
              Seasonal / religious tags (Arbaeen, Ashura, 3 Shaban, 11vi …) that admins attach
              to offline inventory cycles. Active labels appear on Special Deal cards as a coloured ribbon.
            </p>
          </div>
          <button
            data-testid="occasion-create-btn"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#659A9B] hover:bg-[#1B375B] text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> New Label
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex items-center gap-3 text-sm">
          <span className="font-semibold text-gray-700">Filter:</span>
          {[
            { key: 'all',  label: 'All' },
            { key: 'true', label: 'Active' },
            { key: 'false',label: 'Inactive' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterActive(f.key)}
              data-testid={`occasion-filter-${f.key}`}
              className={`px-3 py-1 rounded-full border transition ${filterActive === f.key ? 'bg-[#1B375B] text-white border-[#1B375B]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block w-10 h-10 border-4 border-[#659A9B] border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-gray-600">Loading labels…</p>
            </div>
          ) : labels.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No occasion labels yet. Create your first one to tag offline inventory.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="occasion-labels-table">
              <thead className="bg-gray-50 text-left text-gray-600 uppercase tracking-wide text-xs">
                <tr>
                  <th className="px-4 py-3">Order</th>
                  <th className="px-4 py-3">Label</th>
                  <th className="px-4 py-3">Slug</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {labels.map((lab) => (
                  <tr key={lab.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`occasion-row-${lab.slug}`}>
                    <td className="px-4 py-3 text-gray-500">{lab.display_order ?? 100}</td>
                    <td className="px-4 py-3">
                      <Ribbon name={lab.name} color={lab.color} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{lab.slug}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-md">
                      {lab.description || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {lab.active ? (
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 text-xs font-semibold">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(lab)}
                          data-testid={`occasion-edit-${lab.slug}`}
                          className="p-1.5 text-gray-600 hover:text-[#1B375B] hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {lab.active && (
                          <button
                            onClick={() => handleDelete(lab)}
                            data-testid={`occasion-delete-${lab.slug}`}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Soft delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="occasion-form-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-[#1B375B]">
                {editingId ? 'Edit Occasion Label' : 'New Occasion Label'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  data-testid="occasion-form-name"
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      name,
                      // Auto-fill slug only when creating and slug untouched / derived
                      slug: editingId ? prev.slug : (prev.slug === '' || prev.slug === slugify(prev.name)) ? slugify(name) : prev.slug,
                    }));
                  }}
                  placeholder="e.g. Arbaeen"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Slug {editingId && <span className="text-xs text-gray-500 font-normal">(immutable)</span>}
                </label>
                <input
                  type="text"
                  value={form.slug}
                  data-testid="occasion-form-slug"
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                  placeholder="e.g. arbaeen"
                  disabled={Boolean(editingId)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] font-mono text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase letters, numbers and hyphens only. Used as the stable reference key.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ribbon Colour</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((opt) => (
                    <ColorSwatch
                      key={opt.token}
                      token={opt.token}
                      selected={form.color === opt.token}
                      onClick={() => setForm({ ...form, color: opt.token })}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <span className="text-xs text-gray-500 mr-2">Preview:</span>
                  <Ribbon name={form.name || 'Preview'} color={form.color} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  data-testid="occasion-form-description"
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional context shown in admin tooltips (e.g. 'Annual pilgrimage to Karbala — Safar 20')"
                  rows={2}
                  maxLength={240}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={form.display_order}
                    data-testid="occasion-form-order"
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower = earlier in list.</p>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      data-testid="occasion-form-active"
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="w-5 h-5 text-[#659A9B] rounded focus:ring-2 focus:ring-[#659A9B]"
                    />
                    <span className="text-sm font-semibold text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="occasion-form-submit"
                  className="flex items-center gap-2 px-4 py-2 bg-[#659A9B] hover:bg-[#1B375B] text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      {editingId ? 'Save Changes' : 'Create Label'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OccasionLabelsAdmin;
